import { useState, useEffect, useRef, useCallback } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || '';

interface Suggestion {
  name: string;
  mapbox_id: string;
  feature_type: string;
  address?: string;
  full_address?: string;
  place_formatted?: string;
  poi_category?: string[];
  distance?: number;
}

interface LocationSearchBarProps {
  onLocationSelect: (center: [number, number], placeName: string) => void;
  userLocation: [number, number] | null;
}

// Generate a random session token for Mapbox billing grouping
function generateSessionToken(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

export function LocationSearchBar({ onLocationSelect, userLocation }: LocationSearchBarProps) {
  const [options, setOptions] = useState<Suggestion[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionTokenRef = useRef(generateSessionToken());

  // Suggest (autocomplete)
  const searchSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      let url = `https://api.mapbox.com/search/searchbox/v1/suggest?q=${encodeURIComponent(query)}&language=pt&limit=8&session_token=${sessionTokenRef.current}&access_token=${MAPBOX_TOKEN}`;
      
      if (userLocation) {
        url += `&proximity=${userLocation[0]},${userLocation[1]}`;
      }

      const response = await fetch(url);
      const data = await response.json();
      const filtered = (data.suggestions || []).filter((s: any) => s.feature_type !== 'country' && s.feature_type !== 'region');
        setOptions(filtered);
    } catch (err) {
      console.error('[LocationSearchBar] Suggest error:', err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [userLocation]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { searchSuggestions(inputValue); }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [inputValue, searchSuggestions]);

  // Retrieve coordinates when user selects a suggestion
  const handleSelect = async (suggestion: Suggestion) => {
    setSelecting(true);
    try {
      const url = `https://api.mapbox.com/search/searchbox/v1/retrieve/${suggestion.mapbox_id}?session_token=${sessionTokenRef.current}&access_token=${MAPBOX_TOKEN}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const coords = data.features[0].geometry.coordinates as [number, number];
        const name = suggestion.name || suggestion.full_address || '';
        onLocationSelect(coords, name);
        setInputValue('');
        setOptions([]);
        // Generate new session token for next search
        sessionTokenRef.current = generateSessionToken();
      }
    } catch (err) {
      console.error('[LocationSearchBar] Retrieve error:', err);
    } finally {
      setSelecting(false);
    }
  };

  return (
    <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 10, width: { xs: 'calc(100% - 32px)', sm: 380 } }}>
      <Autocomplete
        freeSolo
        options={options}
        getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
        loading={loading || selecting}
        inputValue={inputValue}
        onInputChange={(_, newValue) => setInputValue(newValue)}
        onChange={(_, value) => {
          if (value && typeof value !== 'string') {
            handleSelect(value);
          }
        }}
        filterOptions={(x) => x}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.mapbox_id}>
            <Box>
              <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.place_formatted || option.full_address || option.address || ''}
              </Typography>
              {option.poi_category && option.poi_category.length > 0 && (
                <Typography variant="caption" color="primary.main" display="block">
                  {option.poi_category.slice(0, 2).join(' / ')}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Buscar restaurantes, parques, pontos turisticos..."
            size="small"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 2,
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {(loading || selecting) && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}
